import { CustomerAddress } from 'types/geolocationTypes';
import { MAIL_REGEX, VAT_NUMBER_REGEX } from 'utils/regexHelpers';
import { addressValidationHandler } from 'utils/addressHelpers';
import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';
import { leaveSignedOut } from 'utils/signedOut';
import { OutOfDateGuard, refusedOrVersion, withBasedOn } from 'api/keptVersion';

/**
 * General settings store themselves (auto-save; scenes 15 and 21, D18, D34,
 * D35, D45; Steven's answers of 5 Oct 2026).
 *
 * One of the four settings screens on which NOTHING is stored while a field
 * is empty or wrong. Two fields ask before they change anything: a SHORTER
 * deletion period (it lets candidates' data be deleted for good) and the
 * two-factor switch (it changes how everyone at the customer logs in).
 * Only people with finance rights can change the screen; the server itself
 * refuses anyone else.
 */
export interface GeneralSettingsWork {
  name: string;
  invoicing_name: string;
  invoicing_email: string;
  invoice_address: CustomerAddress | null | undefined;
  vat: string | null;
  legal_form: string;
  default_po_number: string | null;
  gdpr_deletion_period: number | string | null | undefined;
  two_factor_enabled: boolean;
}

export const NO_FINANCE_RIGHTS =
  'Only people with finance rights can change this.';

/** Scene 28, the description's words (5c). */
export const INVOICING_NOT_COMPLETE =
  'The invoicing details are not complete. Only people with finance rights can fill them in.';

const text = (value: unknown): string => String(value ?? '').trim();

/** The deletion period as a whole number of months, or null when it is none. */
export const monthsOf = (value: unknown): number | null => {
  const months = Number(value);
  return text(value) !== '' && Number.isInteger(months) ? months : null;
};

/** What keeps the screen from being stored, in Steven's words (5 Oct 2026). */
export const missingForGeneralSettings = (
  work: GeneralSettingsWork
): string[] => {
  const months = monthsOf(work.gdpr_deletion_period);
  const email = text(work.invoicing_email);
  const vat = text(work.vat);

  return [
    ...(text(work.name) === '' ? ['company name is required'] : []),
    ...(text(work.invoicing_name) === '' ? ['invoicing name is required'] : []),
    ...(work.invoice_address &&
    addressValidationHandler(true, work.invoice_address) === true
      ? []
      : ['the invoicing address is not complete']),
    ...(email === '' ? ['invoicing e-mail is required'] : []),
    ...(email !== '' && !MAIL_REGEX.test(email)
      ? ['the invoicing e-mail is not a valid address']
      : []),
    ...(vat !== '' && !VAT_NUMBER_REGEX.test(vat)
      ? ['the VAT number is not valid']
      : []),
    ...(text(work.legal_form) === '' ? ['legal form is required'] : []),
    ...(months === null || months < 12 || months > 96
      ? ['the deletion period has to be between 12 and 96 months']
      : []),
  ];
};

/**
 * The question before a shorter deletion period is stored. Storing deletes
 * nothing by itself: the server's nightly job does, at midnight, by each
 * candidate's last application (wording: Steven, 8 Oct 2026).
 */
export const shortenQuestion = (months: number): string =>
  `Shorten to ${months} months? Tonight, at midnight, candidates whose last application is older than that are deleted for good.`;

/** The question before the two-factor switch changes. */
export const twoFactorQuestion = (on: boolean): string =>
  on
    ? 'Everyone will need a code sent by e-mail to log in. Switch on?'
    : 'Nobody will need a code any more. Switch off?';

export const customerPath = (customerId: number): string =>
  `/v4/customers/${customerId}`;

/** The body the Save button sent: the customer as loaded, with the work over it. */
export const generalSettingsBody = (
  customer: Record<string, unknown>,
  work: GeneralSettingsWork
): Record<string, unknown> => ({
  ...customer,
  ...work,
  gdpr_deletion_period: monthsOf(work.gdpr_deletion_period),
  // As the Save button did: the office address follows the invoicing one.
  office_address: work.invoice_address,
});

/** Stores the settings, quietly. @throws when the server did not confirm */
export const saveGeneralSettings = async (
  customerId: number,
  body: Record<string, unknown>,
  signal?: AbortSignal,
  guard?: OutOfDateGuard
): Promise<void> => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';
  const response = await fetch(`${base}${customerPath(customerId)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
      'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
    },
    body: JSON.stringify(withBasedOn(body, guard?.basedOn)),
    signal,
  });
  if (response.status === 401) {
    if (window.location.pathname !== '/') leaveSignedOut();
    throw new Error('The session has ended.');
  }
  // D10: out of date is not a failure; the person chooses.
  const version = await refusedOrVersion(response);
  if (response.status === 403) throw new Error(NO_FINANCE_RIGHTS);
  if (!response.ok) {
    throw new Error(`The settings were not stored (${response.status}).`);
  }
  const answer = (await response.json().catch(() => ({}))) as {
    success?: boolean;
  };
  if (answer.success === false) {
    throw new Error('The settings were not stored.');
  }
  guard?.stored(version);
};
